// File: components/layout/MainLayout.js

import React, { useState, useEffect } from 'react';

/**
 * MainLayout Component
 * 
 * Overall application structure with header, sidebar, and content areas
 * Handles responsive layout and sidebar state management
 * 
 * Props:
 * - topBar: TopBar component
 * - sidebar: Sidebar component
 * - content: Main content area
 * - bottomBar: Optional bottom action bar
 * - backToTop: Optional back to top button
 * - sidebarOpen: Control sidebar from parent (optional)
 * - onSidebarToggle: Callback when sidebar opens/closes
 */
const MainLayout = ({ 
    topBar,
    sidebar,
    content,
    bottomBar = null,
    backToTop = null,
    sidebarOpen = false,
    onSidebarToggle = null
}) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(sidebarOpen);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth < 768
    );

    // Detect mobile/desktop
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            // Auto-close sidebar only when crossing from desktop to mobile
            setIsMobile(prevMobile => {
                if (mobile && !prevMobile) {
                    setIsSidebarOpen(false);
                }
                return mobile;
            });
        };

        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Sync with parent state
    useEffect(() => {
        setIsSidebarOpen(sidebarOpen);
    }, [sidebarOpen]);

    // Listen for sidebar toggle events
    useEffect(() => {
        const handleToggleSidebar = (event) => {
            const newState = event.detail?.open !== undefined 
                ? event.detail.open 
                : !isSidebarOpen;
            
            setIsSidebarOpen(newState);
            
            if (onSidebarToggle) {
                onSidebarToggle(newState);
            }
        };

        window.addEventListener('toggle-sidebar', handleToggleSidebar);
        return () => window.removeEventListener('toggle-sidebar', handleToggleSidebar);
    }, [isSidebarOpen, onSidebarToggle]);

    // Close sidebar (mobile overlay)
    const handleCloseSidebar = () => {
        if (isMobile) {
            setIsSidebarOpen(false);
            if (onSidebarToggle) {
                onSidebarToggle(false);
            }
        }
    };

    // Close sidebar on content click (mobile)
    const handleContentClick = () => {
        if (isMobile && isSidebarOpen) {
            handleCloseSidebar();
        }
    };

    // Prevent body scroll when sidebar is open on mobile
    useEffect(() => {
        if (isMobile && isSidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobile, isSidebarOpen]);

    return (
        <div className={`main-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'} ${topBar ? 'has-topbar' : 'no-topbar'}`}>
            {/* Top Bar */}
            {topBar && (
                <div className="layout-topbar">
                    {topBar}
                </div>
            )}

            {/* Main Content Area */}
            <div className="layout-body">
                {/* Sidebar */}
                <div 
                    className={`layout-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {sidebar}
                </div>

                {/* Overlay (mobile only) */}
                {isMobile && isSidebarOpen && (
                    <div 
                        className="sidebar-overlay"
                        onClick={handleCloseSidebar}
                        aria-label="Close sidebar"
                    />
                )}

                {/* Content */}
                <main 
                    className="layout-content"
                    onClick={handleContentClick}
                >
                    {content}
                </main>
            </div>

            {/* Bottom Bar */}
            {bottomBar && (
                <div className="layout-bottombar">
                    {bottomBar}
                </div>
            )}

            {/* Back to Top Button */}
            {backToTop && (
                <div className="layout-backtotop">
                    {backToTop}
                </div>
            )}
        </div>
    );
};

export default MainLayout;
